import type { MapCanvasParams, RouteMap, RouteWithAction } from '@/types'
import { buildRoutePoints } from './mapUtil'
import { computeLoadedFlags } from './routeUtil'
import { getPathMidPoint, LOADED_BADGE_COLOR } from './drawUtil'

const THUMBNAIL_SIZE = 64
export const THUMBNAIL_RENDER_SIZE = 400
const PADDING = 4

let sharedCanvas: HTMLCanvasElement | null = null
let sharedCtx: CanvasRenderingContext2D | null = null

let cachedBgImage: HTMLImageElement | null = null

export function setCachedBgImage(img: HTMLImageElement): void {
  cachedBgImage = img
}

export function getCachedBgImage(): HTMLImageElement | null {
  return cachedBgImage
}

export function clearCachedBgImage(): void {
  cachedBgImage = null
}

function getSharedCanvas(size: number): {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
} | null {
  if (!sharedCanvas || sharedCanvas.width !== size) {
    sharedCanvas = document.createElement('canvas')
    sharedCanvas.width = size
    sharedCanvas.height = size
    sharedCtx = sharedCanvas.getContext('2d')
  }
  if (!sharedCtx) return null
  return { canvas: sharedCanvas, ctx: sharedCtx }
}

interface ThumbnailOptions {
  routes: RouteWithAction[]
  map: RouteMap
  canvasParams: MapCanvasParams
  size?: number
}

function calculateBounds(
  map: RouteMap,
  routes: RouteWithAction[]
): { minX: number; maxX: number; minY: number; maxY: number } | null {
  const points: { x: number; y: number }[] = []

  for (const route of routes) {
    const startSlotId = route.startSlot || route.slots[0]
    const endSlotId = route.endSlot || route.slots[1] || route.slots[0]
    const routeStations = buildRoutePoints(map, startSlotId, endSlotId)
    if (routeStations) {
      for (const station of routeStations) {
        if (station.pose) {
          points.push({ x: station.pose.x, y: station.pose.y })
        }
      }
    }
  }

  if (points.length === 0) return null

  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity
  for (const p of points) {
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
    if (p.y < minY) minY = p.y
    if (p.y > maxY) maxY = p.y
  }

  const width = maxX - minX
  const height = maxY - minY
  const maxDim = Math.max(width, height, 1)
  const margin = maxDim * 0.15
  const MIN_BOUNDS_SIZE = 2.0
  const centerX = (minX + maxX) / 2
  const centerY = (minY + maxY) / 2
  const halfSize = Math.max(maxDim / 2 + margin, MIN_BOUNDS_SIZE / 2)

  return {
    minX: centerX - halfSize,
    maxX: centerX + halfSize,
    minY: centerY - halfSize,
    maxY: centerY + halfSize,
  }
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  canvasSize: number,
  worldToCanvas: (x: number, y: number) => { x: number; y: number },
  canvasToWorld: (x: number, y: number) => { x: number; y: number }
): void {
  ctx.strokeStyle = 'rgba(200, 200, 200, 0.5)'
  ctx.lineWidth = 0.5

  const topLeft = canvasToWorld(0, 0)
  const bottomRight = canvasToWorld(canvasSize, canvasSize)

  const minX = Math.floor(Math.min(topLeft.x, bottomRight.x)) - 1
  const maxX = Math.ceil(Math.max(topLeft.x, bottomRight.x)) + 1
  const minY = Math.floor(Math.min(topLeft.y, bottomRight.y)) - 1
  const maxY = Math.ceil(Math.max(topLeft.y, bottomRight.y)) + 1

  ctx.beginPath()
  for (let x = minX; x <= maxX; x += 1.0) {
    const top = worldToCanvas(x, maxY)
    const bottom = worldToCanvas(x, minY)
    ctx.moveTo(top.x, top.y)
    ctx.lineTo(bottom.x, bottom.y)
  }
  for (let y = minY; y <= maxY; y += 1.0) {
    const left = worldToCanvas(minX, y)
    const right = worldToCanvas(maxX, y)
    ctx.moveTo(left.x, left.y)
    ctx.lineTo(right.x, right.y)
  }
  ctx.stroke()
}

function drawBackgroundImage(
  ctx: CanvasRenderingContext2D,
  bgImage: HTMLImageElement,
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  canvasParams: MapCanvasParams,
  canvasSize: number
): void {
  const boundsWidth = bounds.maxX - bounds.minX
  const boundsHeight = bounds.maxY - bounds.minY
  const centerX = (bounds.minX + bounds.maxX) / 2
  const centerY = (bounds.minY + bounds.maxY) / 2

  const srcX =
    canvasParams.imageOriginPixX +
    (centerX - boundsWidth / 2) * canvasParams.imagePixelPerMeter
  const srcY =
    canvasParams.imageOriginPixY -
    (centerY + boundsHeight / 2) * canvasParams.imagePixelPerMeter
  const srcWidth = boundsWidth * canvasParams.imagePixelPerMeter
  const srcHeight = boundsHeight * canvasParams.imagePixelPerMeter

  const drawArea = canvasSize - PADDING * 2
  ctx.drawImage(
    bgImage,
    srcX,
    srcY,
    srcWidth,
    srcHeight,
    PADDING,
    PADDING,
    drawArea,
    drawArea
  )
}

function drawForkliftMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  theta: number,
  color: string,
  size: number
): void {
  const bodyW = size * 1.2
  const bodyH = size * 1.1
  const forkLen = size * 1.1
  const forkW = size * 0.25
  const forkGap = size * 0.55
  const cwW = size * 1.2
  const cwH = size * 0.4
  const r = size * 0.1

  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(-theta - Math.PI / 2)
  ctx.translate(0, size * 0.5)

  ctx.fillStyle = color

  ctx.beginPath()
  ctx.roundRect(
    -forkGap / 2 - forkW / 2,
    -bodyH / 2 - forkLen + 15 * (size / 50),
    forkW,
    forkLen,
    r
  )
  ctx.fill()
  ctx.beginPath()
  ctx.roundRect(
    forkGap / 2 - forkW / 2,
    -bodyH / 2 - forkLen + 15 * (size / 50),
    forkW,
    forkLen,
    r
  )
  ctx.fill()

  ctx.beginPath()
  ctx.roundRect(-bodyW / 2, -bodyH / 2, bodyW, bodyH, r)
  ctx.fill()

  ctx.beginPath()
  ctx.roundRect(-cwW / 2, bodyH / 2 - cwH * 0.8, cwW, cwH, r)
  ctx.fill()

  ctx.restore()
}

export function generateThumbnail(options: ThumbnailOptions): string | null {
  const { routes, map, canvasParams, size = THUMBNAIL_RENDER_SIZE } = options

  if (!map || routes.length === 0) return null

  const bounds = calculateBounds(map, routes)
  if (!bounds) return null

  const shared = getSharedCanvas(size)
  if (!shared) return null
  const { canvas, ctx } = shared

  const boundsWidth = bounds.maxX - bounds.minX
  const boundsHeight = bounds.maxY - bounds.minY
  const maxDimension = Math.max(boundsWidth, boundsHeight, 0.1)

  const drawArea = size - PADDING * 2
  const scale = drawArea / maxDimension

  const centerX = (bounds.minX + bounds.maxX) / 2
  const centerY = (bounds.minY + bounds.maxY) / 2

  const worldToCanvas = (x: number, y: number) => ({
    x: PADDING + (x - centerX) * scale + drawArea / 2,
    y: PADDING + (centerY - y) * scale + drawArea / 2,
  })

  const canvasToWorld = (cx: number, cy: number) => ({
    x: (cx - PADDING - drawArea / 2) / scale + centerX,
    y: centerY - (cy - PADDING - drawArea / 2) / scale,
  })

  ctx.fillStyle = '#e5e7eb'
  ctx.fillRect(0, 0, size, size)

  const bgImage = getCachedBgImage()
  if (bgImage) {
    drawBackgroundImage(ctx, bgImage, bounds, canvasParams, size)
  }

  drawGrid(ctx, size, worldToCanvas, canvasToWorld)

  const sizeScale = size / THUMBNAIL_SIZE
  const LINE_WIDTH = 1.2 * sizeScale
  const routeOutlineWidth = 0.75 * sizeScale

  // シナリオの始点（最初のルートの始点）にフォークリフトマーカーを描画
  const markerSize = Math.max(4 * sizeScale, Math.min(scale, 16 * sizeScale))
  const firstRoute = routes[0]
  const firstStartSlotId = firstRoute.startSlot || firstRoute.slots[0]
  const firstStartSlot = map.slots.get(firstStartSlotId)
  if (firstStartSlot) {
    const station = map.stations.get(firstStartSlot.stationId)
    if (station?.pose) {
      const pos = worldToCanvas(station.pose.x, station.pose.y)
      drawForkliftMarker(
        ctx,
        pos.x,
        pos.y,
        station.pose.theta ?? 0,
        'pink',
        markerSize
      )
    }
  }

  // 各ルートを順に描画
  let scenarioStartPoint: { x: number; y: number } | null = null
  const arrowHeadSize = 4 * sizeScale
  const arrowShaftWidth = 1.5 * sizeScale
  const outlineWidth = 0.75 * sizeScale
  const minArrowLength = 15 * sizeScale

  // 荷あり区間（荷積み後〜荷下ろしまで・荷物あり移動）を事前計算
  const loadedFlags = computeLoadedFlags(routes)
  const loadedBadgePositions: { x: number; y: number }[] = []

  for (const [routeIndex, route] of routes.entries()) {
    const startSlotId = route.startSlot || route.slots[0]
    const endSlotId = route.endSlot || route.slots[1] || route.slots[0]
    const routeStations = buildRoutePoints(map, startSlotId, endSlotId)
    if (!routeStations || routeStations.length < 2) continue

    const points = routeStations
      .filter((s) => s.pose)
      .map((s) => worldToCanvas(s.pose!.x, s.pose!.y))
    if (points.length < 2) continue

    if (scenarioStartPoint === null) scenarioStartPoint = points[0]

    // 荷ありルートのバッジ位置（パス中間点）を記録し、全ルート描画後に描く
    if (loadedFlags[routeIndex]) {
      loadedBadgePositions.push(getPathMidPoint(points))
    }

    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.setLineDash([])

    const buildPath = () => {
      ctx.beginPath()
      ctx.moveTo(points[0].x, points[0].y)
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y)
      }
    }

    // White outline
    ctx.strokeStyle = 'white'
    ctx.lineWidth = LINE_WIDTH + routeOutlineWidth * 2
    buildPath()
    ctx.stroke()

    // Blue line
    ctx.strokeStyle = '#3b82f6'
    ctx.lineWidth = LINE_WIDTH
    buildPath()
    ctx.stroke()

    // Arrow at end
    let totalLen = 0
    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x
      const dy = points[i].y - points[i - 1].y
      totalLen += Math.sqrt(dx * dx + dy * dy)
    }

    if (totalLen >= minArrowLength) {
      const lastStart = points[points.length - 2]
      const lastEnd = points[points.length - 1]
      const angle = Math.atan2(lastEnd.y - lastStart.y, lastEnd.x - lastStart.x)

      const tipX = lastEnd.x
      const tipY = lastEnd.y
      const baseX = tipX - arrowHeadSize * 0.5 * Math.cos(angle)
      const baseY = tipY - arrowHeadSize * 0.5 * Math.sin(angle)
      const leftX = baseX - arrowHeadSize * Math.cos(angle - Math.PI / 4)
      const leftY = baseY - arrowHeadSize * Math.sin(angle - Math.PI / 4)
      const rightX = baseX - arrowHeadSize * Math.cos(angle + Math.PI / 4)
      const rightY = baseY - arrowHeadSize * Math.sin(angle + Math.PI / 4)

      ctx.strokeStyle = 'white'
      ctx.lineWidth = arrowShaftWidth + outlineWidth * 3
      ctx.beginPath()
      ctx.moveTo(tipX, tipY)
      ctx.lineTo(leftX, leftY)
      ctx.moveTo(tipX, tipY)
      ctx.lineTo(rightX, rightY)
      ctx.stroke()

      ctx.strokeStyle = '#3b82f6'
      ctx.lineWidth = arrowShaftWidth
      ctx.beginPath()
      ctx.moveTo(tipX, tipY)
      ctx.lineTo(leftX, leftY)
      ctx.moveTo(tipX, tipY)
      ctx.lineTo(rightX, rightY)
      ctx.stroke()
    }
  }

  // 荷ありルートのパス中間点に「荷」バッジを描画（白地に青縁・青文字の四角）
  const badgeHalf = 2.5 * sizeScale
  for (const midPos of loadedBadgePositions) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
    ctx.fillRect(
      midPos.x - badgeHalf,
      midPos.y - badgeHalf,
      badgeHalf * 2,
      badgeHalf * 2
    )
    ctx.strokeStyle = LOADED_BADGE_COLOR
    ctx.lineWidth = 0.6 * sizeScale
    ctx.strokeRect(
      midPos.x - badgeHalf,
      midPos.y - badgeHalf,
      badgeHalf * 2,
      badgeHalf * 2
    )
    ctx.fillStyle = LOADED_BADGE_COLOR
    ctx.font = `bold ${3.8 * sizeScale}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('荷', midPos.x, midPos.y)
  }

  // シナリオの始点を赤丸で表示（最後に描画）
  if (scenarioStartPoint) {
    const dotRadius = 3 * sizeScale
    ctx.fillStyle = 'red'
    ctx.beginPath()
    ctx.arc(
      scenarioStartPoint.x,
      scenarioStartPoint.y,
      dotRadius,
      0,
      Math.PI * 2
    )
    ctx.fill()
  }

  return canvas.toDataURL('image/jpeg', 0.85)
}
